import React from 'react';
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import { isNumber } from 'lodash';
import TabLink from '@dashboard/components/TabLink';
import Link from '@dashboard/components/Link';
import { useStatistics } from '../queries';
import { useStatisticsContext } from '../Dashboard';
import { formatTimestamp, formatValue } from '../dates';
import { formatPrice } from '@dashboard/services/formatting';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { StatMetric, StatTooltip, StatBody, StatChart } from './StatCard';

function sumArray(array = []) {
  return array.reduce((acc, item) => acc + (isNumber(item.value) ? item.value : 0), 0);
}

const numberFormatter = value => Intl.NumberFormat('us').format(value).toString();

const formatUSD = amount => formatPrice({ amount, currency: 'USD' });

export default function CartRecovery() {
  const { unit, range } = useStatisticsContext();
  const { seller } = useAuth();
  const isCartAbandonmentEnabled = seller?.checkoutAbandonment?.sendEmails;
  const { data: abandonedData, isLoading: isLoadingAbandoned } =
    useStatistics('numberOfAbandonedCarts');
  const { data: revenueData, isLoading: isLoadingRevenue } = useStatistics('recoveredRevenue');
  const { data: emailsSentData, isLoading: isLoadingEmailsSent } = useStatistics(
    'numberOfRecoveryEmailsSent',
  );

  const totalAbandoned = sumArray(abandonedData);
  const totalRecoveredRevenue = sumArray(revenueData);
  const totalEmailsSent = sumArray(emailsSentData);

  const revenueChartData = revenueData?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    Revenue: formatValue(timestamp, value),
  }));

  const abandonedChartData = abandonedData?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    Abandoned: formatValue(timestamp, value),
  }));

  const emailsSentChartData = emailsSentData?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    'Emails sent': formatValue(timestamp, value),
  }));

  const maxRevenue = revenueData && Math.max(...revenueData?.map(({ value }) => value), 0);
  const maxAbandoned = abandonedData && Math.max(...abandonedData?.map(({ value }) => value), 0);
  const maxEmailsSent = emailsSentData && Math.max(...emailsSentData?.map(({ value }) => value), 0);

  return (
    <div>
      <TabGroup>
        <TabList className="flex gap-x-8 w-full mb-2">
          <Tab as="div" className="outline-none cursor-pointer">
            {({ selected }) => (
              <TabLink selected={selected}>
                <StatMetric
                  isLoading={isLoadingRevenue}
                  label="Recovered"
                  tooltip={
                    !isCartAbandonmentEnabled ? (
                      <StatTooltip>
                        <Link href="/settings/cart-abandonment" variant="light">
                          Enable cart abandonment emails
                        </Link>{' '}
                        to recover lost revenue.
                      </StatTooltip>
                    ) : null
                  }
                  value={formatUSD(totalRecoveredRevenue)}
                />
              </TabLink>
            )}
          </Tab>

          <Tab as="div" className="outline-none cursor-pointer">
            {({ selected }) => (
              <TabLink selected={selected}>
                <StatMetric
                  isLoading={isLoadingAbandoned}
                  label="Abandoned"
                  value={numberFormatter(totalAbandoned)}
                />
              </TabLink>
            )}
          </Tab>

          <Tab as="div" className="outline-none cursor-pointer">
            {({ selected }) => (
              <TabLink selected={selected}>
                <StatMetric
                  isLoading={isLoadingEmailsSent}
                  label="Emails sent"
                  value={numberFormatter(totalEmailsSent)}
                />
              </TabLink>
            )}
          </Tab>
        </TabList>

        <StatBody>
          <TabPanels>
            <TabPanel>
              <StatChart
                isLoading={isLoadingRevenue}
                data={revenueChartData}
                categories={['Revenue']}
                maxValue={formatUSD(maxRevenue)}
                minValue={formatUSD(0)}
                valueFormatter={formatUSD}
              />
            </TabPanel>

            <TabPanel>
              <StatChart
                isLoading={isLoadingAbandoned}
                data={abandonedChartData}
                categories={['Abandoned']}
                maxValue={maxAbandoned}
                minValue={0}
              />
            </TabPanel>

            <TabPanel>
              <StatChart
                isLoading={isLoadingEmailsSent}
                data={emailsSentChartData}
                categories={['Emails sent']}
                maxValue={maxEmailsSent}
                minValue={0}
              />
            </TabPanel>
          </TabPanels>
        </StatBody>
      </TabGroup>
    </div>
  );
}
